import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { notificationService } from '../services/notificationService';
import { useAuthStore } from '../store/authStore';

/**
 * Prévient l'équipe quand les notifications automatiques ne peuvent pas partir
 * (crédits à 0, canal désactivé, envois récents en échec) — jusqu'ici ces échecs
 * étaient silencieux et les clients ne recevaient simplement rien.
 */
export const NotificationHealthBanner: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role);
  const eligible = role === 'admin_entreprise' || role === 'manager_hub';
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('notif_health_dismissed') === '1';
    } catch {
      return false;
    }
  });

  const { data } = useQuery({
    queryKey: ['notif-health'],
    queryFn: () => notificationService.getHealth(),
    enabled: eligible,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!eligible || dismissed || !data || data.problems.length === 0) return null;

  return (
    <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4">
      <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm text-amber-100">
        <p className="font-semibold">Vos notifications clients risquent de ne pas partir</p>
        <ul className="list-disc list-inside text-amber-100/80 mt-1 space-y-0.5">
          {data.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-4 mt-2 text-xs">
          <Link to="/wallet" className="underline text-amber-200">Recharger mon portefeuille</Link>
          <Link to="/settings" className="underline text-amber-200">Brancher mon WhatsApp Business</Link>
          <Link to="/notifications" className="underline text-amber-200">Voir l'historique des envois</Link>
        </div>
      </div>
      <button
        onClick={() => {
          setDismissed(true);
          try {
            sessionStorage.setItem('notif_health_dismissed', '1');
          } catch {
            /* ignore */
          }
        }}
        className="text-amber-200/70 hover:text-amber-100"
        aria-label="Masquer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
